// Story 8.3 (ADO-2012), D-1: which cart a basket call works on. A Shopper's cart is keyed by
// the depot AND the Trading Account they order for there (`customerAccountCode`, checked by the
// server against what their ordering entry offers), so colleagues of one Customer share it and
// nobody else reaches it. The parts always travel together, so a call can never pair a depot
// with another depot's account.
//
// Enabler E8-G (ADO-3109), DA-8 (AC9): the cart is keyed by the account AND the range, and the
// range comes from the kind of the ordering entry the cart is reached through (`entryKind`).
// Chill (`DEPOT` at depot 25) and Fruit & Veg (`FRUIT_AND_VEG` at depot 25) are two carts, and an
// LSN or WOW cart is reached only with its own kind. The server reads an absent kind as `DEPOT`,
// so the kind goes on the wire only when it is not `DEPOT`. DA-10 (AC11): a staff user works in a
// store account's cart too, so a staff call names the account it picked; a target with no account
// (`null`) is one where none is chosen yet, and the server refuses it rather than guessing.

import type { OrderingEntryKind } from './ordering-choices.client';

/** The cart a basket call works on. */
export interface CartTarget {
  readonly depotId: string;
  /** The active Trading Account's code; `null` when none is chosen yet. */
  readonly customerAccountCode: string | null;
  /** The kind of the ordering entry the cart belongs to (DA-8). */
  readonly entryKind: OrderingEntryKind;
}

/** The depot and the account alone: what the reads that are keyed by the account but not the
 *  range take (`/basket/cut-off-status`, `/basket/order-reference-rule`,
 *  `/basket/fulfilment-options`). Chill and Fruit & Veg at depot 25 share one of each. */
export type CartAccount = Pick<CartTarget, 'depotId' | 'customerAccountCode'>;

/** The depot and the account as query params (see `CartAccount`). */
export function cartAccountQuery(cart: CartAccount): string {
  const depot = `depotId=${encodeURIComponent(cart.depotId)}`;
  return cart.customerAccountCode === null
    ? depot
    : `${depot}&customerAccountCode=${encodeURIComponent(cart.customerAccountCode)}`;
}

/** The cart as query params (the cart reads and the deletes): `depotId`, then the account when
 *  there is one, then the entry kind when it is not `DEPOT`. */
export function cartTargetQuery(cart: CartTarget): string {
  const account = cartAccountQuery(cart);
  return cart.entryKind === 'DEPOT'
    ? account
    : `${account}&entryKind=${encodeURIComponent(cart.entryKind)}`;
}

/** The cart as body fields (the other writes): no `customerAccountCode` key with no account, no
 *  `entryKind` key for a `DEPOT` entry. */
export function cartTargetBody(cart: CartTarget): {
  readonly depotId: string;
  readonly customerAccountCode?: string;
  readonly entryKind?: OrderingEntryKind;
} {
  return {
    depotId: cart.depotId,
    ...(cart.customerAccountCode === null ? {} : { customerAccountCode: cart.customerAccountCode }),
    ...(cart.entryKind === 'DEPOT' ? {} : { entryKind: cart.entryKind }),
  };
}

/** Story 8.4 (ADO-2013): the cart's identity as one string, for keying per-cart state (a Value
 *  Centre's two accounts are two carts). Enabler E8-G (ADO-3109), DA-8: a kind other than `DEPOT`
 *  is part of it, so Chill and Fruit & Veg at depot 25 never share per-cart state. A target with
 *  no account has no account part, so it never collides with an account's. */
export function cartTargetKey(cart: CartTarget): string {
  const account = cartAccountKey(cart);
  return cart.entryKind === 'DEPOT' ? account : `${account}#${cart.entryKind}`;
}

/** The depot and the account as one string (see `CartAccount`): the key of the per-account
 *  state, such as the cut-off read, that Chill and Fruit & Veg share. It equals `cartTargetKey`
 *  for a `DEPOT` entry. */
export function cartAccountKey(cart: CartAccount): string {
  return cart.customerAccountCode === null
    ? cart.depotId
    : `${cart.depotId}|${cart.customerAccountCode}`;
}

/** Whether two targets name the same cart: the depot, the account (Story 8.4, ADO-2013) AND the
 *  entry kind (Enabler E8-G, ADO-3109, DA-8). */
export function sameCartTarget(left: CartTarget | null, right: CartTarget | null): boolean {
  if (left === null || right === null) return left === right;
  return (
    left.depotId === right.depotId &&
    left.customerAccountCode === right.customerAccountCode &&
    left.entryKind === right.entryKind
  );
}
